/**
 * Unified Popup OAuth Utility
 * Handles OAuth flows in popups instead of redirects to maintain page state
 */

interface PopupOAuthOptions {
  width?: number;
  height?: number;
  pollInterval?: number;
  maxAttempts?: number;
  popupTitle?: string;
}

interface PopupOAuthResult {
  success: boolean;
  data?: any;
  error?: string;
  popup?: Window | null;
}

interface OAuthEventData {
  type: string;
  success: boolean;
  data?: any;
  error?: string;
}

export class PopupOAuthManager {
  private static instance: PopupOAuthManager;
  private activePopups: Map<string, Window> = new Map();
  private eventListeners: Map<string, (event: MessageEvent) => void> = new Map();

  static getInstance(): PopupOAuthManager {
    if (!PopupOAuthManager.instance) {
      PopupOAuthManager.instance = new PopupOAuthManager();
    }
    return PopupOAuthManager.instance;
  }

  private constructor() {
    // Listen for postMessage events from OAuth popups
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  private handleMessage(event: MessageEvent) {
    // Only handle messages from trusted origins
    if (!this.isTrustedOrigin(event.origin)) {
      return;
    }

    const { type, success, data, error } = event.data as OAuthEventData;
    
    // Handle OAuth completion messages
    if (type && type.includes('_AUTH_')) {
      console.log(`🔄 OAuth message received:`, { type, success, data, error });
      
      // Emit custom event for components to listen to
      const customEvent = new CustomEvent('oauthComplete', {
        detail: { type, success, data, error }
      });
      window.dispatchEvent(customEvent);
    }
  }

  private isTrustedOrigin(origin: string): boolean {
    const trustedOrigins = [
      'http://localhost:3000',
      'https://login.microsoftonline.com',
      'https://accounts.google.com',
      'https://www.facebook.com',
      'https://www.instagram.com'
    ];
    return trustedOrigins.some(trusted => origin.startsWith(trusted));
  }

  /**
   * Start OAuth flow in a popup with polling
   */
  async startOAuth(
    authUrl: string,
    pollUrl: string,
    authHeaders: any | (() => Promise<any>),
    options: PopupOAuthOptions = {}
  ): Promise<PopupOAuthResult> {
    const {
      width = 600,
      height = 700,
      pollInterval = 2000,
      maxAttempts = 30,
      popupTitle = 'OAuth Authentication'
    } = options;

    return new Promise((resolve) => {
      try {
        // Calculate popup position (center of screen)
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);

        // Open popup
        const popup = window.open(
          authUrl,
          popupTitle,
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popup) {
          resolve({ success: false, error: 'Popup was blocked by browser' });
          return;
        }

        // Store popup reference
        const popupId = Date.now().toString();
        this.activePopups.set(popupId, popup);

        let pollingStopped = false;
        let currentAttempt = 0;

        // Check if popup is closed by user
        const popupCheck = setInterval(() => {
          if (popup.closed) {
            console.log('❌ Popup was closed by user');
            pollingStopped = true;
            clearInterval(popupCheck);
            this.activePopups.delete(popupId);
            resolve({ success: false, error: 'User closed popup' });
          }
        }, 500);

        // Polling function to check auth status
        const poll = async () => {
          if (pollingStopped || currentAttempt >= maxAttempts) {
            clearInterval(popupCheck);
            this.activePopups.delete(popupId);
            if (!popup.closed) popup.close();
            resolve({ 
              success: false, 
              error: currentAttempt >= maxAttempts ? 'Authentication timeout' : 'Polling stopped' 
            });
            return;
          }

          try {
            // Get headers (either static object or from function)
            let headers: any;
            if (typeof authHeaders === 'function') {
              console.log('📡 Getting fresh authentication headers...');
              headers = await authHeaders();
            } else {
              headers = authHeaders;
            }

            console.log(`📊 OAuth poll attempt ${currentAttempt + 1}/${maxAttempts} - Polling ${pollUrl}`);
            console.log('📡 Using headers:', { ...headers, Authorization: headers.Authorization ? '***HIDDEN***' : undefined });

            const response = await fetch(pollUrl, {
              method: 'GET',
              headers: headers
            });

            console.log(`📊 Poll response status: ${response.status}`);

            // Handle different response status codes
            if (!response.ok) {
              const errorText = await response.text();
              console.error(`❌ Poll request failed with status ${response.status}:`, errorText);
              
              // Try to parse error response
              let errorData;
              try {
                errorData = JSON.parse(errorText);
              } catch {
                errorData = { error: errorText };
              }
              
              // Handle authentication errors specifically
              if (response.status === 401 || response.status === 403) {
                pollingStopped = true;
                clearInterval(popupCheck);
                this.activePopups.delete(popupId);
                if (!popup.closed) popup.close();
                resolve({ 
                  success: false, 
                  error: errorData.error || errorData.message || `Authentication failed (${response.status})` 
                });
                return;
              }
              
              // For 500 errors, continue polling for a few more attempts
              if (response.status === 500 && currentAttempt < 5) {
                console.log('🔄 Server error, retrying...');
                currentAttempt++;
                setTimeout(poll, pollInterval);
                return;
              }
              
              // For other errors, fail after a few attempts
              if (currentAttempt >= 3) {
                pollingStopped = true;
                clearInterval(popupCheck);
                this.activePopups.delete(popupId);
                if (!popup.closed) popup.close();
                resolve({ 
                  success: false, 
                  error: errorData.error || errorData.message || `Server error (${response.status})` 
                });
                return;
              }
            }

            const data = await response.json();
            console.log('📊 Poll response data:', data);

            // Check if authentication completed successfully
            if (data.success || data.isConnected || data.authenticated) {
              console.log('✅ OAuth authentication successful');
              pollingStopped = true;
              clearInterval(popupCheck);
              this.activePopups.delete(popupId);
              
              // Close popup after short delay to show success
              setTimeout(() => {
                if (!popup.closed) popup.close();
              }, 1500);

              resolve({ 
                success: true, 
                data: data,
                popup 
              });
              return;
            }

            // Check for error in response
            if (data.error) {
              console.error('❌ OAuth error from server:', data.error);
              pollingStopped = true;
              clearInterval(popupCheck);
              this.activePopups.delete(popupId);
              if (!popup.closed) popup.close();
              resolve({ 
                success: false, 
                error: data.error 
              });
              return;
            }

            console.log(`📊 OAuth still pending, attempt ${currentAttempt + 1}/${maxAttempts}`);
            currentAttempt++;

            if (!pollingStopped && currentAttempt < maxAttempts) {
              setTimeout(poll, pollInterval);
            }
          } catch (error) {
            console.error('❌ Error polling OAuth status:', error);
            currentAttempt++;
            if (!pollingStopped && currentAttempt < maxAttempts) {
              setTimeout(poll, pollInterval);
            } else {
              clearInterval(popupCheck);
              this.activePopups.delete(popupId);
              if (!popup.closed) popup.close();
              resolve({ 
                success: false, 
                error: 'Network error during authentication' 
              });
            }
          }
        };

        // Start polling after popup opens
        setTimeout(poll, pollInterval);

      } catch (error) {
        console.error('❌ Error starting OAuth:', error);
        resolve({ 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    });
  }

  /**
   * Close all active popups
   */
  closeAllPopups(): void {
    this.activePopups.forEach((popup, id) => {
      if (!popup.closed) {
        popup.close();
      }
    });
    this.activePopups.clear();
  }

  /**
   * Get number of active popups
   */
  getActivePopupCount(): number {
    // Clean up closed popups
    this.activePopups.forEach((popup, id) => {
      if (popup.closed) {
        this.activePopups.delete(id);
      }
    });
    return this.activePopups.size;
  }
}

/**
 * Simplified function for components to use popup OAuth
 */
export async function popupOAuth(
  authUrl: string,
  pollUrl: string,
  authHeaders: any | (() => Promise<any>),
  options: PopupOAuthOptions = {}
): Promise<PopupOAuthResult> {
  const manager = PopupOAuthManager.getInstance();
  return await manager.startOAuth(authUrl, pollUrl, authHeaders, options);
}

/**
 * Event emitter for OAuth state changes
 */
export class OAuthEventEmitter {
  private listeners: Map<string, ((data: any) => void)[]> = new Map();

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  emit(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => callback(data));
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const oauthEvents = new OAuthEventEmitter(); 